import { useEffect, useState } from 'react';
import axios from 'axios';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Calendar } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { formatDate, formatCurrency } from '../../lib/utils';
import { 
    Calendar as CalendarIcon, 
    Ticket, 
    Users, 
    TrendingUp, 
    TrendingDown,
    Download,
    Filter,
    BarChart3,
    PieChart as PieChartIcon
} from 'lucide-react';
import { 
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
    LineChart, Line, PieChart, Pie, Cell, AreaChart, Area 
} from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;

export default function OperatorAnalytics() {
    const [loading, setLoading] = useState(true);
    const [dateRange, setDateRange] = useState('7d');
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [analytics, setAnalytics] = useState(null);

    useEffect(() => {
        fetchAnalytics();
    }, [dateRange, startDate, endDate]);

    const fetchAnalytics = async () => {
        setLoading(true);
        try {
            let params = new URLSearchParams();
            if (dateRange !== 'custom') {
                params.append('range', dateRange);
            } else if (startDate && endDate) {
                params.append('start_date', startDate.toISOString());
                params.append('end_date', endDate.toISOString());
            }
            
            const response = await axios.get(`${API}/api/operator/analytics?${params}`, { withCredentials: true });
            setAnalytics(response.data);
        } catch (error) {
            console.error('Failed to fetch analytics:', error);
            // Generate mock data for demonstration
            setAnalytics(generateMockData(dateRange));
        } finally {
            setLoading(false);
        }
    };

    const generateMockData = (range) => {
        const days = range === '7d' ? 7 : range === '30d' ? 30 : range === '90d' ? 90 : 7;
        const dailyData = [];
        const now = new Date();
        
        for (let i = days - 1; i >= 0; i--) {
            const date = new Date(now);
            date.setDate(date.getDate() - i);
            dailyData.push({
                date: date.toISOString().split('T')[0],
                redemptions: Math.floor(Math.random() * 20) + 5,
                codes_generated: Math.floor(Math.random() * 15) + 3
            });
        }

        const totalRedemptions = dailyData.reduce((sum, d) => sum + d.redemptions, 0);
        const totalCodes = dailyData.reduce((sum, d) => sum + d.codes_generated, 0);
        const prevPeriodRedemptions = totalRedemptions * (0.8 + Math.random() * 0.4);

        return {
            summary: {
                total_redemptions: totalRedemptions,
                total_codes_generated: totalCodes,
                active_codes: Math.floor(totalCodes * 0.4),
                redemption_rate: ((totalRedemptions / totalCodes) * 100).toFixed(1),
                change_percentage: (((totalRedemptions - prevPeriodRedemptions) / prevPeriodRedemptions) * 100).toFixed(1)
            },
            daily_data: dailyData,
            by_attraction: [
                { name: 'Colosseum', redemptions: Math.floor(totalRedemptions * 0.35) },
                { name: 'Vatican Museums', redemptions: Math.floor(totalRedemptions * 0.28) },
                { name: 'Trevi Fountain', redemptions: Math.floor(totalRedemptions * 0.20) },
                { name: 'Pantheon', redemptions: Math.floor(totalRedemptions * 0.17) }
            ],
            by_language: [
                { name: 'English', value: 45, color: '#D4AF37' },
                { name: 'Italian', value: 25, color: '#E5D3B3' },
                { name: 'Spanish', value: 15, color: '#94A3B8' },
                { name: 'French', value: 10, color: '#64748B' },
                { name: 'Other', value: 5, color: '#475569' }
            ]
        };
    };

    const dateRangeOptions = [
        { value: '7d', label: 'Last 7 days' },
        { value: '30d', label: 'Last 30 days' },
        { value: '90d', label: 'Last 90 days' },
        { value: 'custom', label: 'Custom range' }
    ];

    if (loading && !analytics) {
        return (
            <DashboardLayout type="operator">
                <div className="flex items-center justify-center h-64">
                    <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
                </div>
            </DashboardLayout>
        );
    }

    const { summary, daily_data, by_attraction, by_language } = analytics || {};
    const isPositiveChange = parseFloat(summary?.change_percentage || 0) >= 0;

    return (
        <DashboardLayout type="operator">
            <div className="space-y-8" data-testid="operator-analytics">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                        <h1 className="font-outfit text-2xl lg:text-3xl font-bold text-white">Analytics</h1>
                        <p className="text-muted-foreground mt-1">Track your performance and insights</p>
                    </div>
                    
                    {/* Date Range Selector */}
                    <div className="flex items-center gap-3">
                        <Select value={dateRange} onValueChange={setDateRange}>
                            <SelectTrigger className="w-[180px] bg-[#121620] border-white/10" data-testid="date-range-select">
                                <CalendarIcon className="w-4 h-4 mr-2 text-muted-foreground" />
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-[#121620] border-white/10">
                                {dateRangeOptions.map(opt => (
                                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        
                        {dateRange === 'custom' && (
                            <div className="flex items-center gap-2">
                                <Popover>
                                    <PopoverTrigger asChild>
                                        <Button variant="outline" className="border-white/10 bg-[#121620]">
                                            {startDate ? formatDate(startDate) : 'Start date'}
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-auto p-0 bg-[#121620] border-white/10">
                                        <Calendar
                                            mode="single"
                                            selected={startDate}
                                            onSelect={setStartDate}
                                            initialFocus
                                        />
                                    </PopoverContent>
                                </Popover>
                                <span className="text-muted-foreground">to</span>
                                <Popover>
                                    <PopoverTrigger asChild>
                                        <Button variant="outline" className="border-white/10 bg-[#121620]">
                                            {endDate ? formatDate(endDate) : 'End date'}
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-auto p-0 bg-[#121620] border-white/10">
                                        <Calendar
                                            mode="single"
                                            selected={endDate}
                                            onSelect={setEndDate}
                                            initialFocus
                                        />
                                    </PopoverContent>
                                </Popover>
                            </div>
                        )}
                    </div>
                </div>

                {/* Summary Stats */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <Card className="bg-[#121620] border-white/10">
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between mb-3">
                                <Users className="w-5 h-5 text-blue-400" />
                                {isPositiveChange ? (
                                    <div className="flex items-center gap-1 text-emerald-400 text-xs">
                                        <TrendingUp className="w-3 h-3" />
                                        +{summary?.change_percentage}%
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-1 text-red-400 text-xs">
                                        <TrendingDown className="w-3 h-3" />
                                        {summary?.change_percentage}%
                                    </div>
                                )}
                            </div>
                            <p className="text-2xl font-bold text-white">{summary?.total_redemptions || 0}</p>
                            <p className="text-xs text-muted-foreground mt-1">Total Redemptions</p>
                        </CardContent>
                    </Card>
                    
                    <Card className="bg-[#121620] border-white/10">
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between mb-3">
                                <Ticket className="w-5 h-5 text-gold" />
                            </div>
                            <p className="text-2xl font-bold text-white">{summary?.total_codes_generated || 0}</p>
                            <p className="text-xs text-muted-foreground mt-1">Codes Generated</p>
                        </CardContent>
                    </Card>
                    
                    <Card className="bg-[#121620] border-white/10">
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between mb-3">
                                <Ticket className="w-5 h-5 text-emerald-400" />
                            </div>
                            <p className="text-2xl font-bold text-white">{summary?.active_codes || 0}</p>
                            <p className="text-xs text-muted-foreground mt-1">Active Codes</p>
                        </CardContent>
                    </Card>
                    
                    <Card className="bg-[#121620] border-white/10">
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between mb-3">
                                <BarChart3 className="w-5 h-5 text-purple-400" />
                            </div>
                            <p className="text-2xl font-bold text-white">{summary?.redemption_rate || 0}%</p>
                            <p className="text-xs text-muted-foreground mt-1">Redemption Rate</p>
                        </CardContent>
                    </Card>
                </div>

                {/* Charts Row */}
                <div className="grid lg:grid-cols-2 gap-6">
                    {/* Redemptions Over Time */}
                    <Card className="bg-[#121620] border-white/10">
                        <CardHeader>
                            <CardTitle className="text-white font-outfit flex items-center gap-2">
                                <TrendingUp className="w-5 h-5 text-gold" />
                                Redemptions Over Time
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="h-[300px]">
                                <ResponsiveContainer width="100%" height="100%">
                                    <AreaChart data={daily_data}>
                                        <defs>
                                            <linearGradient id="redemptionGradient" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#D4AF37" stopOpacity={0.3}/>
                                                <stop offset="95%" stopColor="#D4AF37" stopOpacity={0}/>
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#1A202C" />
                                        <XAxis 
                                            dataKey="date" 
                                            stroke="#94A3B8" 
                                            fontSize={12}
                                            tickFormatter={(value) => {
                                                const date = new Date(value);
                                                return `${date.getMonth() + 1}/${date.getDate()}`;
                                            }}
                                        />
                                        <YAxis stroke="#94A3B8" fontSize={12} />
                                        <Tooltip 
                                            contentStyle={{ 
                                                backgroundColor: '#121620', 
                                                border: '1px solid rgba(255,255,255,0.1)',
                                                borderRadius: '8px'
                                            }}
                                            labelStyle={{ color: '#fff' }}
                                        />
                                        <Area 
                                            type="monotone" 
                                            dataKey="redemptions" 
                                            stroke="#D4AF37" 
                                            strokeWidth={2}
                                            fill="url(#redemptionGradient)" 
                                        />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Codes Generated vs Redeemed */}
                    <Card className="bg-[#121620] border-white/10">
                        <CardHeader>
                            <CardTitle className="text-white font-outfit flex items-center gap-2">
                                <BarChart3 className="w-5 h-5 text-gold" />
                                Codes Generated vs Redeemed
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="h-[300px]">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={daily_data}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#1A202C" />
                                        <XAxis 
                                            dataKey="date" 
                                            stroke="#94A3B8" 
                                            fontSize={12}
                                            tickFormatter={(value) => {
                                                const date = new Date(value);
                                                return `${date.getMonth() + 1}/${date.getDate()}`;
                                            }}
                                        />
                                        <YAxis stroke="#94A3B8" fontSize={12} />
                                        <Tooltip 
                                            contentStyle={{ 
                                                backgroundColor: '#121620', 
                                                border: '1px solid rgba(255,255,255,0.1)',
                                                borderRadius: '8px'
                                            }}
                                            labelStyle={{ color: '#fff' }}
                                        />
                                        <Bar dataKey="codes_generated" fill="#64748B" name="Generated" radius={[4, 4, 0, 0]} />
                                        <Bar dataKey="redemptions" fill="#D4AF37" name="Redeemed" radius={[4, 4, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Bottom Charts */}
                <div className="grid lg:grid-cols-2 gap-6">
                    {/* Top Attractions */}
                    <Card className="bg-[#121620] border-white/10">
                        <CardHeader>
                            <CardTitle className="text-white font-outfit">Top Attractions</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-4">
                                {by_attraction?.map((attraction, index) => {
                                    const maxRedemptions = Math.max(...by_attraction.map(a => a.redemptions));
                                    const percentage = (attraction.redemptions / maxRedemptions) * 100;
                                    return (
                                        <div key={attraction.name} className="space-y-2">
                                            <div className="flex items-center justify-between text-sm">
                                                <span className="text-white">{index + 1}. {attraction.name}</span>
                                                <span className="text-gold font-medium">{attraction.redemptions}</span>
                                            </div>
                                            <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                                                <div 
                                                    className="h-full bg-gold rounded-full transition-all duration-500"
                                                    style={{ width: `${percentage}%` }}
                                                />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </CardContent>
                    </Card>

                    {/* By Language */}
                    <Card className="bg-[#121620] border-white/10">
                        <CardHeader>
                            <CardTitle className="text-white font-outfit flex items-center gap-2">
                                <PieChartIcon className="w-5 h-5 text-gold" />
                                Redemptions by Language
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="flex items-center gap-6">
                                <div className="w-[180px] h-[180px]">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie
                                                data={by_language}
                                                cx="50%"
                                                cy="50%"
                                                innerRadius={50}
                                                outerRadius={80}
                                                paddingAngle={2}
                                                dataKey="value"
                                            >
                                                {by_language?.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={entry.color} />
                                                ))}
                                            </Pie>
                                            <Tooltip 
                                                contentStyle={{ 
                                                    backgroundColor: '#121620', 
                                                    border: '1px solid rgba(255,255,255,0.1)',
                                                    borderRadius: '8px'
                                                }}
                                            />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                                <div className="flex-1 space-y-3">
                                    {by_language?.map((lang) => (
                                        <div key={lang.name} className="flex items-center justify-between text-sm">
                                            <div className="flex items-center gap-2">
                                                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: lang.color }} />
                                                <span className="text-muted-foreground">{lang.name}</span>
                                            </div>
                                            <span className="text-white font-medium">{lang.value}%</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </DashboardLayout>
    );
}
